"use strict";
/**
 * @license
 * Copyright 2017 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { registerEventListener } from "#src/util/disposable.js";
import { HierarchicalMap } from "#src/util/hierarchical_map.js";
export var Modifiers = /* @__PURE__ */ ((Modifiers2) => {
  Modifiers2[Modifiers2["CONTROL"] = 1] = "CONTROL";
  Modifiers2[Modifiers2["ALT"] = 2] = "ALT";
  Modifiers2[Modifiers2["META"] = 4] = "META";
  Modifiers2[Modifiers2["SHIFT"] = 8] = "SHIFT";
  return Modifiers2;
})(Modifiers || {});
export function getEventModifierMask(event) {
  return (event.ctrlKey ? 1 /* CONTROL */ : 0) | (event.altKey ? 2 /* ALT */ : 0) | (event.metaKey ? 4 /* META */ : 0) | (event.shiftKey ? 8 /* SHIFT */ : 0);
}
export function getStrokeIdentifier(keyName, modifiers) {
  let identifier = "";
  if (modifiers & 1 /* CONTROL */) {
    identifier += "control+";
  }
  if (modifiers & 2 /* ALT */) {
    identifier += "alt+";
  }
  if (modifiers & 4 /* META */) {
    identifier += "meta+";
  }
  if (modifiers & 8 /* SHIFT */) {
    identifier += "shift+";
  }
  identifier += keyName;
  return identifier;
}
function getStrokeIdentifierWithOptionalModifiers(keyName, modifiers, optionalModifiers) {
  let identifier = "";
  if (modifiers & 1 /* CONTROL */) {
    identifier += "control+";
  }
  if (optionalModifiers & 1 /* CONTROL */) {
    identifier += "control?+";
  }
  if (modifiers & 2 /* ALT */) {
    identifier += "alt+";
  }
  if (optionalModifiers & 2 /* ALT */) {
    identifier += "alt?+";
  }
  if (modifiers & 4 /* META */) {
    identifier += "meta+";
  }
  if (optionalModifiers & 4 /* META */) {
    identifier += "meta?+";
  }
  if (modifiers & 8 /* SHIFT */) {
    identifier += "shift+";
  }
  if (optionalModifiers & 8 /* SHIFT */) {
    identifier += "shift?+";
  }
  identifier += keyName;
  return identifier;
}
function parseEventIdentifier(identifier) {
  const firstColonOffset = identifier.indexOf(":");
  let phase;
  if (firstColonOffset !== -1) {
    phase = identifier.substring(0, firstColonOffset);
    if (phase !== "at" && phase !== "bubble") {
      throw new Error(`Invalid event phase: ${JSON.stringify(phase)}`);
    }
  }
  const parts = identifier.substring(firstColonOffset + 1).split("+");
  let keyName;
  let modifiers = 0;
  let optionalModifiers = 0;
  loop: for (const part of parts) {
    switch (part) {
      case "control":
        modifiers |= 1 /* CONTROL */;
        break;
      case "control?":
        optionalModifiers |= 1 /* CONTROL */;
        break;
      case "alt":
        modifiers |= 2 /* ALT */;
        break;
      case "alt?":
        optionalModifiers |= 2 /* ALT */;
        break;
      case "meta":
        modifiers |= 4 /* META */;
        break;
      case "meta?":
        optionalModifiers |= 4 /* META */;
        break;
      case "shift":
        modifiers |= 8 /* SHIFT */;
        break;
      case "shift?":
        optionalModifiers |= 8 /* SHIFT */;
        break;
      default:
        if (keyName === void 0) {
          keyName = part;
        } else {
          keyName = void 0;
          break loop;
        }
    }
  }
  if (keyName === void 0 || modifiers & optionalModifiers) {
    throw new Error(`Invalid event identifier: ${JSON.stringify(identifier)}`);
  }
  return {
    phase,
    keyName,
    modifiers,
    optionalModifiers
  };
}
function* getNormalizedStrokeIdentifiers(keyName, modifiers, optionalModifiers) {
  if (optionalModifiers === 0) {
    yield getStrokeIdentifier(keyName, modifiers);
  }
  for (let m = 0; m < 16; ++m) {
    if ((m & (modifiers | optionalModifiers)) !== m) continue;
    if ((m & modifiers) !== modifiers) continue;
    yield getStrokeIdentifier(keyName, m);
  }
}
function* getNormalizedEventIdentifiers(parsed) {
  const { phase } = parsed;
  const strokeIdentifiers = getNormalizedStrokeIdentifiers(
    parsed.keyName,
    parsed.modifiers,
    parsed.optionalModifiers
  );
  if (phase === void 0) {
    for (const suffix of strokeIdentifiers) {
      yield `at:${suffix}`;
      yield `bubble:${suffix}`;
    }
  } else {
    for (const suffix of strokeIdentifiers) {
      yield `${phase}:${suffix}`;
    }
  }
}
export function normalizeEventAction(parsed, action) {
  const identifier = getStrokeIdentifierWithOptionalModifiers(
    parsed.keyName,
    parsed.modifiers,
    parsed.optionalModifiers
  );
  if (typeof action === "string") {
    return { action, originalEventIdentifier: identifier };
  }
  return { ...action, originalEventIdentifier: identifier };
}
function friendlyEventIdentifier(identifier) {
  identifier = identifier.replace(
    /^(?:at|bubble|capture)|(?:(?:shift|control|alt|meta)\?\+)/g,
    ""
  );
  return identifier;
}
export class EventActionMap extends HierarchicalMap {
  /**
   * Returns a new EventActionMap with the specified bindings.
   *
   * The keys of the `bindings` object specify unnormalized event identifiers to be mapped to their
   * corresponding `ActionOrEventAction` values.
   */
  static fromObject(bindings, options = {}) {
    const map = new EventActionMap();
    map.label = options.label;
    if (options.parents !== void 0) {
      for (const [parent, priority] of options.parents) {
        map.addParent(parent, priority);
      }
    }
    for (const key of Object.keys(bindings)) {
      map.set(key, bindings[key]);
    }
    return map;
  }
  setFromObject(bindings) {
    for (const key of Object.keys(bindings)) {
      this.set(key, bindings[key]);
    }
  }
  /**
   * Maps the specified event `identifier` to the specified `action`.
   *
   * The `identifier` may be unnormalized; the actual mapping is created for each corresponding
   * normalized identifier.
   */
  set(identifier, action) {
    const parsedIdentifier = parseEventIdentifier(identifier);
    const normalizedAction = normalizeEventAction(parsedIdentifier, action);
    for (const normalizedIdentifier of getNormalizedEventIdentifiers(
      parsedIdentifier
    )) {
      super.set(normalizedIdentifier, normalizedAction);
    }
  }
  /**
   * Deletes the mapping for the specified `identifier`.
   *
   * The `identifier` may be unnormalized; the mapping is deleted for each corresponding normalized
   * identifier.
   */
  delete(identifier) {
    for (const normalizedIdentifier of getNormalizedEventIdentifiers(
      parseEventIdentifier(identifier)
    )) {
      super.delete(normalizedIdentifier);
    }
  }
  describe() {
    const bindings = [];
    const uniqueBindings = /* @__PURE__ */ new Map();
    for (const [, value] of this.entries()) {
      uniqueBindings.set(value.originalEventIdentifier, value.action);
    }
    const actionBindings = /* @__PURE__ */ new Map();
    for (const [eventIdentifier, action] of uniqueBindings) {
      let events = actionBindings.get(action);
      if (events === void 0) {
        events = [];
        actionBindings.set(action, events);
      }
      events.push(friendlyEventIdentifier(eventIdentifier));
    }
    for (const [action, events] of actionBindings) {
      const eventStr = events.length === 1 ? events[0] : `{${events.join(",")}}`;
      bindings.push(`${eventStr}\u2192${action}`);
    }
    return bindings.join(", ");
  }
}
export function dispatchEventAction(originalEvent, detail, eventAction) {
  if (eventAction === void 0) {
    return;
  }
  if (eventAction.stopPropagation !== false) {
    originalEvent.stopPropagation();
  }
  const actionEvent = new CustomEvent("action:" + eventAction.action, {
    bubbles: true,
    detail,
    cancelable: true
  });
  const cancelled = !originalEvent.target.dispatchEvent(actionEvent);
  if (eventAction.preventDefault !== false || cancelled) {
    originalEvent.preventDefault();
  }
}
export const eventPhaseNames = [];
eventPhaseNames[Event.AT_TARGET] = "at";
eventPhaseNames[Event.CAPTURING_PHASE] = "capture";
eventPhaseNames[Event.BUBBLING_PHASE] = "bubble";
export function dispatchEvent(baseIdentifier, originalEvent, eventPhase, detail, eventMap) {
  const eventIdentifier = eventPhaseNames[eventPhase] + ":" + baseIdentifier;
  const eventAction = eventMap.get(eventIdentifier);
  dispatchEventAction(originalEvent, detail, eventAction);
}
export function dispatchEventWithModifiers(baseIdentifier, originalEvent, detail, eventMap) {
  dispatchEvent(
    getStrokeIdentifier(baseIdentifier, getEventModifierMask(originalEvent)),
    originalEvent,
    originalEvent.eventPhase,
    detail,
    eventMap
  );
}
export function registerActionListener(target, action, listener, options) {
  return registerEventListener(target, `action:${action}`, listener, options);
}
